/**
 * V2 Site Settings page.
 *
 * Reads/writes the consolidated `/api/odl-app/site-settings/{id}/`
 * endpoint via the `features/siteSettings/` hooks.  All sections —
 * general, location, utility, ignore window, alarms, solar (when the
 * site supports it), third-party (admin), billing (admin) — render
 * inline on a single page with a unified Save Changes bar; each
 * section's own fields live under `sections/site/`.
 *
 * Section visibility is read off the response shape itself: solar /
 * third_party / billing keys are only present when the site
 * supports solar / the requester is an admin.  Editability comes
 * from `useUser().permissions.canEditSite` (the user's
 * `pliny.change_site` perm cached on the auth slice).  The same
 * guards apply server-side so a hand-crafted PATCH from a
 * non-admin still gets rejected.
 *
 * The legacy V1 (`SiteSettingsView.jsx`) was removed in
 * `d0678cd83`; the standalone `AlarmSettingsPage` / `SolarOptionsPage`
 * routes still live on for a release before final cleanup (the
 * Solar one — Alarm has been folded in here as of this commit).
 */

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import {
  selectSiteData,
  selectSiteFeatureAssociationByKey,
  selectSiteUnit,
  siteApi,
  siteSettingsRequiresCommandLease,
} from '@sophocles/features/entities/site';
import { selectAlarmDefinitions } from '@sophocles/features/reference';
import { setStructure } from '@sophocles/features/sites/slices/selectedStructure/actions';
import { markStaleSiteSettings, useSiteSettings } from '@sophocles/features/siteSettings';
import { selectSeparateTenant } from '@sophocles/features/tenant/slice';
import { shareVirtualAccess, unshareVirtualAccess } from '@sophocles/features/virtual/api';
import useBreadcrumbs from '@sophocles/hooks/useBreadcrumbs';
import useCustomer from '@sophocles/hooks/useCustomer';
import useSiteCommandWrite from '@sophocles/hooks/useSiteCommandWrite';
import useSiteGroup from '@sophocles/hooks/useSiteGroup';
import { SettingsForm, SettingsPageShell } from '@sophocles/views/settings/components';
import WeatherSection from '@sophocles/views/settings/components/WeatherSection';

import AccessSection from './sections/site/AccessSection';
import AlarmsSection from './sections/site/AlarmsSection';
import BillingSection from './sections/site/BillingSection';
import FeaturesSection from './sections/site/FeaturesSection';
import GeneralSection from './sections/site/GeneralSection';
import LocationSection from './sections/site/LocationSection';
import SolarSection from './sections/site/SolarSection';
import UtilitySection from './sections/site/UtilitySection';

// Yup schema — mirrors the model-level constraints on the pliny
// side so the user gets inline feedback before they hit Save and the
// PATCH never goes out for known-bad values.  `name` / `latitude` /
// `longitude` are the only genuinely-required fields per Site model
// definitions (everything else has blank=True or a default — see the
// `required` audit in SiteSettingsViewV2 R3 round).
//
// Sections are still `.nullable()` at the top level so a partial
// PATCH (only `utility`, etc.) doesn't have to seed every section.
const validationSchema = yup.object({
  general: yup
    .object({
      name: yup.string().trim().required('Name is required.').max(50, 'Name must be 50 characters or fewer.'),
      description: yup.string().nullable(),
      location_description: yup.string().nullable().max(150, 'Location description must be 150 characters or fewer.'),
    })
    .nullable(),
  location: yup
    .object({
      latitude: yup
        .number()
        .typeError('Latitude must be a number.')
        .min(-90, 'Latitude must be between −90 and 90.')
        .max(90, 'Latitude must be between −90 and 90.')
        .required('Latitude is required.'),
      longitude: yup
        .number()
        .typeError('Longitude must be a number.')
        .min(-180, 'Longitude must be between −180 and 180.')
        .max(180, 'Longitude must be between −180 and 180.')
        .required('Longitude is required.'),
      time_zone: yup.string().required('Time Zone is required.'),
    })
    .nullable(),
  utility: yup.object().nullable(),
  ignore_window: yup
    .object({
      enabled: yup.boolean().nullable(),
      start_time: yup
        .string()
        .nullable()
        .when('enabled', {
          is: true,
          then: schema => schema.required('Start Time is required when Quiet Hours is on.'),
        }),
      end_time: yup
        .string()
        .nullable()
        .when('enabled', {
          is: true,
          then: schema => schema.required('End Time is required when Quiet Hours is on.'),
        }),
    })
    .nullable(),
  third_party: yup.object().nullable(),
  alarms: yup.array().nullable(),
  solar: yup.object().nullable(),
  billing: yup.object().nullable(),
  weather: yup
    .object({
      weather_range_km: yup.number().integer().min(0).max(7).nullable(),
      weather_sensitivity: yup.number().integer().min(0).max(100).nullable(),
      relays: yup.array().of(yup.number().integer().min(1).max(4)).nullable(),
      invert_operation: yup.boolean().nullable(),
    })
    .nullable(),
});

function SiteSettingsViewV2() {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useDispatch();
  const siteId = params.site_id;
  const customerId = Number(params.cust_id);
  const siteGroupId = Number(params.sitegroup_id);
  const [sharingSaveVersion, setSharingSaveVersion] = React.useState(0);

  // Seed Redux contexts so selectors downstream (useBreadcrumbs,
  // selectCustomer, getSelectedPlantSelector) have the data they need.
  useCustomer(customerId);
  const { siteGroup } = useSiteGroup(siteGroupId);

  // Seed the selected-structure id so useBreadcrumbs can resolve the
  // site name from the entity store when the entity is already cached
  // (e.g. navigating back from the dashboard). The settings view does
  // not join the site socket room and does not fetch the full site
  // entity — it has its own breadcrumb fallback (settings.general.name)
  // when the entity store is cold.
  React.useEffect(() => {
    if (siteId != null) {
      dispatch(setStructure(siteId));
    }
  }, [dispatch, siteId]);

  const { settings, save } = useSiteSettings({ siteId });
  const { permissions, isStaff } = useUser();
  const autoBreadcrumbs = useBreadcrumbs();
  const canEdit = permissions.canEditSite;
  const site = useSelector(selectSiteData(siteId));
  const unit = useSelector(selectSiteUnit(siteId));
  const alarmDefinitions = useSelector(selectAlarmDefinitions);
  const { commandLocked, runWrite } = useSiteCommandWrite(siteId);

  const separateTenant = useSelector(selectSeparateTenant);
  const deviceSettings = settings?.device_configuration;
  const commandContext = {
    hasRadio:
      unit?.radio != null || (deviceSettings?.has_relay_controller === true && deviceSettings?.radio_id != null),
    hasStandaloneCic:
      (unit == null && site?.has_standalone_cic === true) ||
      (deviceSettings?.has_relay_controller === false && deviceSettings?.has_cic === true),
    alarmDefinitions,
  };

  // Section presence is the signal — see backend
  // SiteSettingsSerializer.to_representation docstring.
  const hasSolar = Boolean(settings?.solar) && !separateTenant;
  // Sharing belongs to the site's primary group. Viewed through a virtual
  // group the site is the shared copy, so there is nothing to administer here.
  // Defaults to virtual while the group entity is cold, matching
  // EditSiteDialog: the sharing surface stays hidden until ownership is known.
  const isVirtualSiteGroup = Boolean(siteGroup.get('is_virtual', true));
  const hasThirdPartySection = Boolean(settings?.third_party) && !isVirtualSiteGroup;
  const hasBilling = !__EXCLUDE_BILLING__ && Boolean(settings?.billing);
  const hasAdminSections = (hasThirdPartySection || hasBilling) && !separateTenant;

  React.useEffect(() => {
    if (siteId == null) return undefined;
    const sub = dispatch(siteApi.endpoints.retrieveSite.initiate({ id: siteId }));
    return () => sub.unsubscribe();
  }, [dispatch, siteId]);

  // Weather
  const weatherAssociation = useSelector(selectSiteFeatureAssociationByKey(siteId, 'WEATHER'));
  const hasWeatherConfig = Boolean(settings?.weather);

  const hasWeatherSection =
    hasWeatherConfig ||
    weatherAssociation?.is_effectively_enabled === true ||
    weatherAssociation?.is_disabled_from_parent === true;

  const weatherEffectiveRef = React.useRef(undefined);
  React.useEffect(() => {
    const cur = weatherAssociation?.is_effectively_enabled;
    const prev = weatherEffectiveRef.current;
    weatherEffectiveRef.current = cur;
    if (prev !== undefined && cur !== undefined && cur !== prev) {
      dispatch(markStaleSiteSettings({ siteId }));
    }
  }, [dispatch, siteId, weatherAssociation?.is_effectively_enabled]);

  const basePath = params.cust_id
    ? `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${siteId}`
    : `/structure/${siteId}`;

  // Sidebar sections — driven by section presence on the response.
  // Memoized so SettingsPageShell's registration effect doesn't
  // re-fire on every render.
  const sections = React.useMemo(() => {
    const list = [
      { id: 'settings-general', title: t('siteSettings.v2.general', 'General'), icon: 'info' },
      { id: 'settings-location', title: t('siteSettings.v2.location', 'Location'), icon: 'locationOn' },
      // Utility & Account is hidden for separate (white-label) tenants.
      ...(separateTenant
        ? []
        : [{ id: 'settings-utility', title: t('siteSettings.v2.utility', 'Utility & Account'), icon: 'receiptLong' }]),
      { id: 'settings-alarms', title: t('siteSettings.v2.alarms', 'Alarms'), icon: 'notificationsActive' },
    ];
    if (hasSolar) {
      list.push({ id: 'settings-solar', title: t('siteSettings.v2.solar', 'Solar'), icon: 'wbSunny' });
    }
    if (hasWeatherSection) {
      list.push({ id: 'settings-weather', title: t('siteSettings.v2.weather', 'Weather'), icon: 'cloud' });
    }
    if (hasThirdPartySection) {
      list.push({
        id: 'settings-third-party',
        title: t('siteSettings.v2.access', 'Sharing'),
        icon: 'verifiedUser',
        pillText: t('siteSettings.staffOnly', 'Staff Only'),
      });
    }
    if (hasBilling) {
      list.push({
        id: 'settings-billing',
        title: t('siteSettings.v2.billing', 'Billing'),
        icon: 'paid',
        pillText: t('siteSettings.staffOnly', 'Staff Only'),
      });
    }
    if (isStaff && !separateTenant) {
      list.push({
        id: 'settings-features',
        title: t('siteSettings.v2.features', 'Features'),
        icon: 'tune',
        pillText: t('siteSettings.staffOnly', 'Staff Only'),
      });
    }
    return list;
  }, [t, hasSolar, hasThirdPartySection, hasBilling, hasWeatherSection, isStaff, separateTenant]);

  if (!settings) {
    return <LoadingScreen />;
  }

  // Mirror the server shape for read-aligned sections.  Solar /
  // third_party / billing are populated only when capabilities allow
  // — Formik treats missing keys as undefined and omits them from
  // the diff so dirty-tracking stays clean.
  const initialValues = {
    general: settings.general || {},
    location: settings.location || {},
    utility: settings.utility || {},
    ignore_window: settings.ignore_window || {},
    alarms: settings.alarms || [],
    ...(hasSolar ? { solar: settings.solar || {} } : {}),
    // Weather config is included when WEATHER is effectively enabled on the
    // backend. When absent (feature off), the key is omitted so the Formik
    // diff stays clean and the PATCH never sends `{"weather": null}`.
    ...(hasWeatherConfig ? { weather: settings.weather } : {}),
    ...(hasThirdPartySection
      ? {
          third_party: settings.third_party || {},
          // Synthetic Formik field — pending share additions /
          // removals are pushed in here by ThirdPartiesTab's onChange
          // so dirty-tracking activates the global Save bar.  Stripped
          // out before the settings PATCH; dispatched as separate
          // share mutations in handleSave.
          __pending_shares: { newlyShared: [], newlyUnshared: [] },
        }
      : {}),
    ...(hasBilling ? { billing: settings.billing || {} } : {}),
  };

  // `canEdit` is derived from OPTIONS metadata in the hook —
  // `actions.PATCH` is populated iff the requester holds
  // `pliny.change_site` for this site.  Server is the authoritative
  // gate; this just lets us short-circuit the Save bar UI.
  //
  // Save flow splits into two: (1) consolidated settings PATCH with
  // every section that changed, (2) per-share virtual access
  // mutations for the rows the user added / removed in
  // ThirdPartiesTab.  The endpoint accepts only the access-allowed
  // scalar — share rows live in a separate API the dialog also calls
  // out to.  Both run sequentially so a failure on either surfaces
  // through SettingsForm's try/catch. Successful mutation responses update
  // the membership store; the version signal clears the tab's pending rows.
  const handleSave = async diff => {
    const { __pending_shares, ...settingsDiff } = diff;
    const hasPendingShareChanges =
      (__pending_shares?.newlyUnshared?.length ?? 0) > 0 || (__pending_shares?.newlyShared?.length ?? 0) > 0;
    if (Object.keys(settingsDiff).length > 0) {
      const requiresCommand = siteSettingsRequiresCommandLease(settings, settingsDiff, commandContext);
      const outcome = await runWrite(() => save(settingsDiff), { requiresCommand });
      if (!outcome.executed) return false;
    }
    if (__pending_shares?.newlyUnshared?.length > 0) {
      await dispatch(unshareVirtualAccess(__pending_shares.newlyUnshared)).unwrap();
    }
    if (__pending_shares?.newlyShared?.length > 0) {
      await dispatch(shareVirtualAccess(__pending_shares.newlyShared)).unwrap();
    }
    if (hasPendingShareChanges) setSharingSaveVersion(version => version + 1);
    return true;
  };

  // Use auto-breadcrumbs when the redux selectors are populated
  // (customer / plant / structure loaded via PlantHOC + StructureHOC),
  // and ensure a site crumb exists from settings.general.name on a
  // hard refresh / deep link where those selectors haven't filled in.
  // Append "Settings" as the active terminus.  Passing explicit
  // breadcrumbs guarantees the trail renders instead of the simple-
  // title fallback.
  const settingsLabel = t('siteSettings.v2.title', 'Settings');
  const breadcrumbs = (() => {
    const autoCrumbs = autoBreadcrumbs.slice();
    const hasSiteCrumb =
      autoCrumbs.length > 0 && autoCrumbs[autoCrumbs.length - 1].to?.includes(`/structure/${siteId}`);
    if (!hasSiteCrumb && params.cust_id && params.sitegroup_id && settings.general?.name) {
      autoCrumbs.push({
        label: settings.general.name,
        to: `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${siteId}`,
      });
    }
    return [...autoCrumbs, { label: settingsLabel }];
  })();

  return (
    <SettingsPageShell
      page="site_settings"
      withAppNav={false}
      breadcrumbs={breadcrumbs}
      contextName="site"
      basePath={basePath}
      sections={sections}
      sectionParam={params.section}
      viewOnly={!canEdit}
      viewOnlyMessage={t(
        'siteSettings.v2.viewOnly',
        "You can view this site's settings but don't have permission to edit. Contact an administrator to request edit access.",
      )}
    >
      <SettingsForm
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSave={handleSave}
        readOnly={!canEdit}
        isSaveDisabled={(values, currentValues) =>
          commandLocked && siteSettingsRequiresCommandLease(currentValues, values, commandContext)
        }
      >
        {/*
              HTML <fieldset disabled> propagates `disabled` to every
              nested form control, so we don't have to thread the flag
              through every SettingsXxxField individually.  Use a Stack
              with `component="fieldset"` so we keep the inter-section
              spacing UserSettingsViewV2 has — a plain Box collapses
              `<Stack spacing={3}>` (which only spaces top-level
              children of <Form>) into a single child.
            */}
        <Stack component="fieldset" disabled={!canEdit} spacing={3} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
          <GeneralSection />

          <LocationSection canEdit={canEdit} />

          {!separateTenant && <UtilitySection />}

          <AlarmsSection disabled={!canEdit} />

          {hasSolar ? <SolarSection /> : null}

          {hasWeatherSection ? <WeatherSection siteId={siteId} disabled={!canEdit} /> : null}

          {hasThirdPartySection ? (
            /*
                  selectThirdPartiesByCustomerId in features/customers/slice
                  does strict-equality on `tp.owner.id === customerId`, and
                  `tp.owner.id` is a Number out of the API.  useParams gives
                  us strings, so coerce here — passing the raw URL string
                  filters every row out and silently disables Add Third
                  Party Share even when shares are available.
                */
            <AccessSection
              customerId={Number(params.cust_id)}
              siteId={Number(siteId)}
              siteGroup={{ id: siteGroup.get('id'), description: siteGroup.get('description') }}
              resetPendingVersion={sharingSaveVersion}
            />
          ) : null}

          {hasBilling ? <BillingSection customerId={Number(params.cust_id)} /> : null}

          {isStaff && !separateTenant ? <FeaturesSection siteId={siteId} /> : null}

          {hasAdminSections ? null : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <AdminPanelSettingsIcon fontSize="small" />
              <Typography variant="caption">
                {t(
                  'siteSettings.v2.adminSectionsHidden',
                  'Third-party access and billing settings are visible to administrators only.',
                )}
              </Typography>
            </Box>
          )}
        </Stack>
      </SettingsForm>
    </SettingsPageShell>
  );
}

export default SiteSettingsViewV2;
